import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription, forkJoin, of } from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { NameInputDialogComponent } from "../../shared/name-input-dialog/name-input-dialog.component";
import { BackgroundService } from "../background.service";
import { DigitalCardService } from "../../digital-cards/digital-card.service";
import type { DigitalCardProfileItem } from "../../digital-cards/digital-cards.interface";
import { CompanyService } from "../../company/company.service";
import type { EmployeePickerItem } from "../../employees/employees.interface";
import type { BackgroundForm } from "../backgrounds.interface";
import {
  BRAND_CANVAS_FONT_PRESETS,
  BRAND_FONT_PRESET_ID,
} from "../../shared/font-presets";
import type { BrandFontPreset } from "../../shared/font-presets.interface";

@Component({
  selector: "app-background-edit",
  templateUrl: "./background-edit.component.html",
  styleUrls: ["./background-edit.component.scss"],
  standalone: false,
})
export class BackgroundEditComponent implements OnInit, OnDestroy {
  loading = true;
  saving = false;
  backgroundId: string | null = null;
  bannerPreviewUrl: string | null = null;
  companyLogoUrl: string | null = null;
  companyFontFamily = "";
  companyFontFileUrl: string | null = null;
  previewQrTarget: string | null = null;
  loadingPreview = false;
  employeePickerReady = false;
  digitalCardTemplates: DigitalCardProfileItem[] = [];
  readonly digitalCardTemplateLabel = (template: DigitalCardProfileItem) =>
    template.displayName || template.profileId || "";
  readonly digitalCardTemplateValue = (template: DigitalCardProfileItem) =>
    template.profileId;
  previewEmployee: EmployeePickerItem | null = null;
  private readonly builtInFontPresets = BRAND_CANVAS_FONT_PRESETS;
  private previewQrSub?: Subscription;
  private pendingFontSettings: {
    fontName?: string;
    fontFamily?: string;
    fontCssUrl?: string | null;
  } | null = null;

  form = new FormGroup<BackgroundForm>({
    name: new FormControl<string>("", {
      nonNullable: true,
    }),
    bannerType: new FormControl<"gradient" | "image">("gradient", {
      nonNullable: true,
    }),
    bannerGradientStart: new FormControl<string>("#ffffff", {
      nonNullable: true,
    }),
    bannerGradientEnd: new FormControl<string>("#f5f5f0", {
      nonNullable: true,
    }),
    qrLabel: new FormControl<string>("", { nonNullable: true }),
    showLogo: new FormControl<boolean>(true, { nonNullable: true }),
    digitalCardProfileId: new FormControl<string | null>(null),
    fontPreset: new FormControl<string>("default", { nonNullable: true }),
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private backgroundService: BackgroundService,
    private digitalCardService: DigitalCardService,
    private companyService: CompanyService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.backgroundId = this.route.snapshot.paramMap.get("backgroundId");
    this.loading = true;
    forkJoin({
      company: this.companyService.get().pipe(catchError(() => of(null))),
      templates: this.digitalCardService
        .getProfiles({ templatesOnly: true })
        .pipe(
          catchError(() => of({ profiles: [] as DigitalCardProfileItem[] })),
        ),
      background: this.backgroundId
        ? this.backgroundService.getOne(this.backgroundId)
        : of(null),
    })
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: ({ company, templates, background }) => {
          this.companyLogoUrl = company?.photoUrl
            ? `${company.photoUrl}?t=${Date.now()}`
            : null;
          this.companyFontFamily =
            (company?.brandFontFamily ?? "").trim() ||
            (company?.brandFontFileName ?? "").trim().replace(/\.[^.]+$/, "") ||
            "";
          this.companyFontFileUrl =
            (company?.brandFontFileUrl ?? "").trim() || null;
          this.digitalCardTemplates = templates.profiles ?? [];

          if (!background) {
            return;
          }

          this.form.patchValue({
            name: background.name,
            bannerType: background.settings?.bannerType ?? "gradient",
            bannerGradientStart:
              background.settings?.bannerGradientStart ?? "#ffffff",
            bannerGradientEnd:
              background.settings?.bannerGradientEnd ?? "#f5f5f0",
            qrLabel: background.settings?.qrLabel ?? "",
            showLogo: background.settings?.showLogo ?? true,
            digitalCardProfileId: background.digitalCardProfileId || null,
          });
          this.patchFontPreset({
            fontName: background.settings?.fontName,
            fontFamily: background.settings?.fontFamily,
            fontCssUrl: background.settings?.fontCssUrl,
          });
          this.bannerPreviewUrl = background.settings?.bannerImageUrl ?? null;
          if (!this.form.get("digitalCardProfileId")?.value) {
            this.employeePickerReady = true;
            this.loadPreviewQrTarget();
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.previewQrSub?.unsubscribe();
  }

  get pageReady(): boolean {
    return !this.loading;
  }

  get canSave(): boolean {
    return true;
  }

  get companyBrandFontReady(): boolean {
    return (
      this.companyFontFamily.trim().length > 0 &&
      (this.companyFontFileUrl?.trim().length ?? 0) > 0
    );
  }

  get fontPresets(): BrandFontPreset[] {
    if (!this.companyBrandFontReady) {
      return this.builtInFontPresets;
    }
    return [
      {
        id: BRAND_FONT_PRESET_ID,
        label: this.companyFontFamily.trim(),
        fontFamily: `"${this.companyFontFamily.replace(/"/g, "")}"`,
      },
      ...this.builtInFontPresets,
    ];
  }

  get selectedFont(): BrandFontPreset {
    return (
      this.fontPresets.find(
        (font) => font.id === this.form.controls.fontPreset.value,
      ) ?? this.fontPresets[0]
    );
  }

  get previewFontFamily(): string {
    return this.selectedFont.fontFamily;
  }

  get previewFontCssUrl(): string | null {
    return this.selectedFont.id === BRAND_FONT_PRESET_ID
      ? null
      : (this.selectedFont.fontCssUrl ?? null);
  }

  get previewCompanyFontFileUrl(): string | null {
    return this.selectedFont.id === BRAND_FONT_PRESET_ID
      ? this.companyFontFileUrl
      : null;
  }

  get pickerTemplateContextId(): string | null {
    const digitalCardProfileId =
      this.form.get("digitalCardProfileId")?.value?.trim() ?? "";
    return digitalCardProfileId.length > 0 ? digitalCardProfileId : null;
  }

  onPreviewEmployeeChange(emp: EmployeePickerItem | null): void {
    this.previewEmployee = emp;
    this.employeePickerReady = true;
    this.loadPreviewQrTarget();
  }

  onEmployeePickerInitialResolved(): void {
    this.employeePickerReady = true;
    this.loadPreviewQrTarget();
  }

  onGradientColorChange(
    controlName: "bannerGradientStart" | "bannerGradientEnd",
    value: string,
  ): void {
    this.form.patchValue({ [controlName]: value });
  }

  private patchFontPreset(settings: {
    fontName?: string;
    fontFamily?: string;
    fontCssUrl?: string | null;
  }): void {
    const fontCssUrl = (settings.fontCssUrl ?? "").trim();
    const fontFamily = (settings.fontFamily ?? "").trim();
    const fontName = (settings.fontName ?? "").trim();
    const familyPrimary = fontFamily
      .split(",")[0]
      ?.trim()
      .replace(/^["']|["']$/g, "");
    const hasExplicitFont = !!(fontCssUrl || fontFamily || fontName);
    let fontPresetId = "default";
    if (
      this.companyBrandFontReady &&
      (!hasExplicitFont ||
        (!fontCssUrl &&
          familyPrimary.toLowerCase() === this.companyFontFamily.toLowerCase()))
    ) {
      fontPresetId = BRAND_FONT_PRESET_ID;
    } else if (fontName) {
      const byName = this.builtInFontPresets.find(
        (font) => font.label.trim().toLowerCase() === fontName.toLowerCase(),
      );
      if (byName) {
        fontPresetId = byName.id;
      }
    }
    if (fontPresetId === "default" && fontFamily) {
      const byFamily = this.builtInFontPresets.find(
        (font) =>
          font.fontFamily.trim().toLowerCase() === fontFamily.toLowerCase(),
      );
      if (byFamily) {
        fontPresetId = byFamily.id;
      }
    }
    this.form.patchValue({ fontPreset: fontPresetId }, { emitEvent: false });
  }

  save(): void {
    if (this.saving || !this.backgroundId) {
      return;
    }
    if (!this.form.get("name")?.value.trim()) {
      this.showNameInputDialog();
      return;
    }
    this.performSave();
  }

  private showNameInputDialog(): void {
    const dialogRef = this.dialog.open(NameInputDialogComponent, {
      width: "500px",
      data: {
        title: this.translate.instant("background.enterName") as string,
        placeholder: this.translate.instant("footers.name") as string,
        currentName: this.form.get("name")?.value || "",
        actionType: "save" as const,
        saveButtonText: this.translate.instant("save") as string,
      },
    });
    dialogRef
      .afterClosed()
      .subscribe((result: { name?: string; action?: string } | undefined) => {
        if (!result?.name?.trim()) {
          return;
        }
        this.form.get("name")?.setValue(result.name.trim());
        this.performSave();
      });
  }

  private performSave(): void {
    if (this.saving || !this.backgroundId) {
      return;
    }
    this.saving = true;
    const selected = this.selectedFont;
    const payload = {
      name: this.form.get("name")?.value.trim() || "",
      digitalCardProfileId:
        this.form.get("digitalCardProfileId")?.value || null,
      settings: {
        bannerType: this.form.get("bannerType")?.value ?? "gradient",
        bannerGradientStart:
          this.form.get("bannerGradientStart")?.value || null,
        bannerGradientEnd: this.form.get("bannerGradientEnd")?.value || null,
        qrLabel: this.form.get("qrLabel")?.value || null,
        showLogo: this.form.get("showLogo")?.value ?? true,
        bannerImageUrl:
          this.form.get("bannerType")?.value === "image"
            ? this.bannerPreviewUrl
            : null,
        fontName: selected.label,
        fontFamily: selected.fontFamily,
        fontCssUrl:
          selected.id === BRAND_FONT_PRESET_ID
            ? null
            : (selected.fontCssUrl ?? null),
      },
    };
    this.backgroundService
      .update(this.backgroundId, payload)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translate.instant("background.saved") as string,
          );
          void this.router.navigate(["/backgrounds"]);
        },
      });
  }

  back(): void {
    void this.router.navigate(["/backgrounds"]);
  }

  uploadBanner = (file: File) =>
    this.backgroundService
      .uploadImage(file)
      .pipe(map((res) => res.bannerImageUrl));

  onBannerFileChange(image: { previewUrl?: string }): void {
    this.bannerPreviewUrl = image.previewUrl ?? null;
    this.form.patchValue({ bannerType: "image" });
  }

  removeBanner(): void {
    this.bannerPreviewUrl = null;
  }

  onDigitalCardProfileChange(): void {
    this.employeePickerReady = false;
    this.previewEmployee = null;
    this.previewQrTarget = null;
    this.loadingPreview = true;
  }

  private loadPreviewQrTarget(): void {
    const profileId =
      this.form.get("digitalCardProfileId")?.value?.trim() || null;
    if (!profileId) {
      this.previewQrTarget =
        globalThis.location?.origin || "https://gsignature.com";
      this.loadingPreview = false;
      return;
    }
    if (!this.employeePickerReady) {
      this.loadingPreview = true;
      return;
    }
    this.loadingPreview = true;
    this.previewQrSub?.unsubscribe();
    const previewEmail = this.previewEmployee?.email?.trim() || null;
    this.previewQrSub = this.backgroundService
      .getPreviewQrTarget(profileId, false, previewEmail)
      .subscribe({
        next: (res) => {
          this.previewQrTarget = res.qrTarget || null;
          this.loadingPreview = false;
        },
        error: () => {
          this.previewQrTarget =
            globalThis.location?.origin || "https://gsignature.com";
          this.loadingPreview = false;
        },
      });
  }
}
